import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, AlertTriangle, Award, Clock, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface TestResult {
  id: string;
  candidate_id: string;
  test_id: string;
  answers: any;
  scores: any;
  completion_time: number | null;
  started_at: string;
  completed_at: string | null;
  status: string;
  personality_profile: any;
  recommendations: string | null;
  test_name?: string;
  test_description?: string;
  scoring_criteria?: any;
}

interface TestInfo {
  name: string;
  description: string;
  scoring_criteria: {
    clinical_scales: Record<string, { name: string; description: string; threshold: number }>;
    validity_scales: Record<string, { name: string; description: string; threshold: number }>;
    interpretation_guidelines: any;
    professional_recommendations: any;
  };
}

interface TestResultsProps {
  candidateId: string;
  candidateName: string;
  onClose: () => void;
}

export function TestResults({ candidateId, candidateName, onClose }: TestResultsProps) {
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Greek translations for test scales and traits
  const getGreekScaleName = (scale: string, testType: string) => {
    const mmpi2Scales: Record<string, string> = {
      'hypochondriasis': 'Υποχονδρίαση',
      'depression': 'Κατάθλιψη',
      'hysteria': 'Υστερία',
      'psychopathic_deviate': 'Ψυχοπαθητική Εκτροπή',
      'masculinity_femininity': 'Αρρενωπότητα-Θηλυκότητα',
      'paranoia': 'Παράνοια',
      'psychasthenia': 'Ψυχασθένεια',
      'schizophrenia': 'Σχιζοφρένεια',
      'hypomania': 'Υπομανία',
      'social_introversion': 'Κοινωνική Εσωστρέφεια',
      'anxiety': 'Άγχος',
      'fears': 'Φόβοι',
      'obsessiveness': 'Ψυχαναγκαστικότητα',
      'depression_clinical': 'Κλινική Κατάθλιψη',
      'health_concerns': 'Ανησυχίες για την Υγεία',
      'bizarre_mentation': 'Παράξενη Σκέψη',
      'anger': 'Θυμός',
      'cynicism': 'Κυνισμός',
      'antisocial_practices': 'Αντικοινωνικές Πρακτικές',
      'type_a': 'Τύπος Α',
      'low_self_esteem': 'Χαμηλή Αυτοεκτίμηση',
      'family_problems': 'Οικογενειακά Προβλήματα',
      'work_interference': 'Παρεμβολή στη Δουλειά',
      'negative_treatment_indicators': 'Αρνητικοί Δείκτες Θεραπείας'
    };

    const ipineoTraits: Record<string, string> = {
      'extraversion': 'Εξωστρέφεια',
      'agreeableness': 'Συνεργασία',
      'conscientiousness': 'Ευσυνειδησία',
      'neuroticism': 'Νευρωτισμός',
      'openness': 'Ανοιχτότητα',
      'integrity': 'Ακεραιότητα',
      'vigilance': 'Επαγρύπνηση',
      'assertiveness': 'Αυταρέσκεια',
      'social_skills': 'Κοινωνικές Δεξιότητες',
      'responsibility': 'Υπευθυνότητα',
      'decision_making': 'Λήψη Αποφάσεων',
      'impulse_control': 'Έλεγχος Παρορμήσεων',
      'paranoia_control': 'Έλεγχος Παράνοιας',
      'stress_tolerance': 'Ανοχή στο Άγχος',
      'authority_respect': 'Σεβασμός Αρχής',
      'aggression_control': 'Έλεγχος Επιθετικότητας',
      'psychological_stability': 'Ψυχολογική Σταθερότητα'
    };

    const additionalScales: Record<string, string> = {
      'leadership': 'Ηγεσία',
      'teamwork': 'Συνεργασία Ομάδας',
      'stress_management': 'Διαχείριση Άγχους',
      'reliability': 'Αξιοπιστία',
      'attention_to_detail': 'Προσοχή στη Λεπτομέρεια',
      'communication': 'Επικοινωνία',
      'adaptability': 'Προσαρμοστικότητα',
      'emotional_stability': 'Συναισθηματική Σταθερότητα'
    };

    if (testType.includes('MMPI-2')) {
      return mmpi2Scales[scale] || scale;
    } else if (testType.includes('IPIP-NEO')) {
      return ipineoTraits[scale] || scale;
    } else {
      return additionalScales[scale] || mmpi2Scales[scale] || ipineoTraits[scale] || scale;
    }
  };

  const getGreekScoreLevel = (score: number, testType: string) => {
    if (testType.includes('MMPI-2')) {
      if (score >= 80) return 'Πολύ Υψηλό';
      if (score >= 65) return 'Υψηλό';
      if (score >= 55) return 'Ελαφρώς Υψηλό';
      if (score >= 45) return 'Μέτριο';
      if (score >= 35) return 'Χαμηλό';
      return 'Πολύ Χαμηλό';
    } else {
      if (score >= 80) return 'Πολύ Υψηλό';
      if (score >= 60) return 'Υψηλό';
      if (score >= 40) return 'Μέτριο';
      if (score >= 20) return 'Χαμηλό';
      return 'Πολύ Χαμηλό';
    }
  };

  useEffect(() => {
    fetchTestResults();
  }, [candidateId]);

  const fetchTestResults = async () => {
    try {
      // Get all test results for this candidate
      const { data: resultData, error: resultError } = await supabase
        .from('test_results')
        .select(`
          *,
          occupational_tests (
            name,
            description,
            scoring_criteria
          )
        `)
        .eq('candidate_id', candidateId)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false });

      if (resultError) throw resultError;

      if (resultData) {
        const formattedResults = resultData.map(result => ({
          ...result,
          test_name: result.occupational_tests?.name || 'Άγνωστο Τεστ',
          test_description: result.occupational_tests?.description || '',
          scoring_criteria: result.occupational_tests?.scoring_criteria || {}
        })) as TestResult[];
        
        setTestResults(formattedResults);
      }
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η φόρτωση των αποτελεσμάτων",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 85) return "text-green-600";
    if (score >= 75) return "text-blue-600";
    if (score >= 65) return "text-yellow-600";
    if (score >= 55) return "text-orange-600";
    return "text-red-600";
  };

  const getScoreBadgeVariant = (score: number) => {
    if (score >= 85) return "default";
    if (score >= 75) return "secondary";
    if (score >= 65) return "outline";
    return "destructive";
  };

  const getValidityIcon = (value: number, threshold: number) => {
    if (value >= threshold) {
      return <AlertTriangle className="w-4 h-4 text-red-500" />;
    }
    return <CheckCircle className="w-4 h-4 text-green-500" />;
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}ώ ${mins}λ`;
    }
    return `${mins} λεπτά`;
  };

  if (loading) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardContent className="p-6">
          <div className="text-center">Φόρτωση αποτελεσμάτων...</div>
        </CardContent>
      </Card>
    );
  }

  if (!testResults || testResults.length === 0) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardContent className="p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Δεν βρέθηκαν αποτελέσματα τεστ για αυτόν τον υποψήφιο.</p>
            <Button onClick={onClose} className="mt-4">
              Επιστροφή
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                Αποτελέσματα Ψυχολογικών Τεστ
              </CardTitle>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {candidateName}
                </div>
                <div>
                  Σύνολο Τεστ: {testResults.length}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchTestResults}>
                Ανανέωση
              </Button>
              <Button variant="outline" onClick={onClose}>
                Κλείσιμο
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Test Results */}
      {testResults.map((testResult, index) => {
        const testInfo = {
          name: testResult.test_name || 'Άγνωστο Τεστ',
          description: testResult.test_description || '',
          scoring_criteria: testResult.scoring_criteria || {}
        };

        return (
          <div key={testResult.id} className="space-y-6">
            {/* Test Header */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  {testInfo.name} - Τεστ #{index + 1}
                </CardTitle>
                <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Διάρκεια: {formatDuration(testResult.completion_time || 0)}
                  </div>
                  <div>
                    Ολοκληρώθηκε: {testResult.completed_at ? new Date(testResult.completed_at).toLocaleDateString('el-GR') : 'Μη διαθέσιμο'}
                  </div>
                </div>
              </CardHeader>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Overall Score */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle>Συνολικό Σκορ</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center space-y-4">
                    <div className={`text-6xl font-bold ${getScoreColor(testResult.scores?.overallScore || 0)}`}>
                      {testResult.scores?.overallScore || 0}
                    </div>
                    <div className="text-2xl font-medium text-muted-foreground">/ 100</div>
                    <Badge variant={getScoreBadgeVariant(testResult.scores?.overallScore || 0)} className="text-sm px-3 py-1">
                      {(testResult.scores?.overallScore || 0) >= 85 ? "Εξαιρετικός" :
                       (testResult.scores?.overallScore || 0) >= 75 ? "Πολύ Καλός" :
                       (testResult.scores?.overallScore || 0) >= 65 ? "Καλός" :
                       (testResult.scores?.overallScore || 0) >= 55 ? "Μέτριος" :
                       (testResult.scores?.overallScore || 0) >= 45 ? "Κάτω του Μετρίου" : "Ανεπαρκής"}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Recommendation */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Σύσταση</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted p-4 rounded-lg">
                    <p className="text-lg leading-relaxed">{testResult.recommendations || 'Δεν υπάρχουν διαθέσιμες συστάσεις'}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Clinical Scales - Show different content based on test type */}
            {testResult.scores?.categoryScores && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    {testInfo.name.includes('MMPI-2') ? 'Κλινικές Κλίμακες MMPI-2 (T-Scores)' : 
                     testInfo.name.includes('IPIP-NEO') ? 'Προσωπικότητα IPIP-NEO' : 
                     'Κλίμακες Αποτελεσμάτων'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {Object.entries(testResult.scores?.categoryScores || {}).map(([scale, score]) => {
                      const numScore = Number(score) || 0;
                      const scaleInfo = testInfo.scoring_criteria?.clinical_scales?.[scale];
                      const isElevated = numScore >= 65;
                      const isCritical = numScore >= 80;
                      
                      return (
                        <div key={scale} className="space-y-2">
                           <div className="flex justify-between items-center">
                             <span className="font-medium text-sm">
                               {getGreekScaleName(scale, testInfo.name)}
                             </span>
                             <span className={`font-bold ${
                               isCritical ? 'text-red-600' :
                               isElevated ? 'text-yellow-600' :
                               'text-green-600'
                             }`}>
                               {testInfo.name.includes('MMPI-2') ? `T=${numScore}` : numScore}
                             </span>
                           </div>
                           <Progress 
                             value={testInfo.name.includes('MMPI-2') ? 
                               Math.min((numScore / 120) * 100, 100) : 
                               Math.min((numScore / 100) * 100, 100)} 
                             className="h-2" 
                           />
                           <div className="text-xs text-muted-foreground">
                             {getGreekScoreLevel(numScore, testInfo.name)} - {scaleInfo?.description || 'Κλίμακα αποτελεσμάτων'}
                           </div>
                           {isElevated && testInfo.name.includes('MMPI-2') && (
                             <div className={`text-xs px-2 py-1 rounded ${
                               isCritical ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                             }`}>
                               {getGreekScoreLevel(numScore, testInfo.name)}
                             </div>
                           )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {testResult.scores?.elevatedScales && testResult.scores.elevatedScales.length > 0 && (
                    <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <h4 className="font-medium text-amber-800 mb-2">Αυξημένες Κλίμακες:</h4>
                      <p className="text-sm text-amber-700">
                        {testResult.scores.elevatedScales.join(', ')}
                      </p>
                    </div>
                  )}
                  
                  {testResult.scores?.clinicalInterpretation && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-medium text-blue-800 mb-2">Κλινική Ερμηνεία:</h4>
                      <p className="text-sm text-blue-700 whitespace-pre-line">
                        • {testResult.scores.clinicalInterpretation}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Validity Scales - Show only for MMPI-2 */}
            {testResult.scores?.validityScores && testInfo.name.includes('MMPI-2') && (
              <Card>
                <CardHeader>
                  <CardTitle>Κλίμακες Εγκυρότητας</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(testResult.scores?.validityScores || {}).map(([scale, value]) => {
                      const numValue = Number(value) || 0;
                      const scaleInfo = testInfo.scoring_criteria?.validity_scales?.[scale];
                      const threshold = scaleInfo?.threshold || 3;
                      const isValid = numValue < threshold;
                      
                      return (
                        <div key={scale} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <div className="font-medium text-sm">
                              {scaleInfo?.description || scale}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Όριο: {threshold}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`font-bold ${isValid ? 'text-green-600' : 'text-red-600'}`}>
                              {numValue}
                            </span>
                            {getValidityIcon(numValue, threshold)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="mt-4 p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Σημείωση:</strong> Οι κλίμακες εγκυρότητας βοηθούν στην αξιολόγηση της αξιοπιστίας των απαντήσεων. 
                      Υψηλές τιμές μπορεί να υποδηλώνουν αμυντικές απαντήσεις ή ασυνέπειες.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Personality Profile - Show only for IPIP-NEO */}
            {testResult.personality_profile && testInfo.name.includes('IPIP-NEO') && (
              <Card>
                <CardHeader>
                  <CardTitle>Προφίλ Προσωπικότητας (Big Five)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                    {Object.entries(testResult.personality_profile).map(([trait, score]) => {
                      const numScore = Number(score) || 0;
                      return (
                        <div key={trait} className="space-y-2">
                           <div className="flex justify-between items-center">
                             <span className="font-medium text-sm">{getGreekScaleName(trait, testInfo.name)}</span>
                             <span className="font-bold">{numScore}</span>
                           </div>
                           <Progress value={numScore} className="h-2" />
                           <div className="text-xs text-muted-foreground">
                             {getGreekScoreLevel(numScore, testInfo.name)}
                           </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Test Information */}
            <Card>
              <CardHeader>
                <CardTitle>Πληροφορίες Τεστ</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div><strong>Όνομα:</strong> {testInfo.name}</div>
                  <div><strong>Περιγραφή:</strong> {testInfo.description}</div>
                  <div><strong>Ημερομηνία Έναρξης:</strong> {new Date(testResult.started_at).toLocaleString('el-GR')}</div>
                  <div><strong>Ημερομηνία Ολοκλήρωσης:</strong> {testResult.completed_at ? new Date(testResult.completed_at).toLocaleString('el-GR') : 'Μη διαθέσιμο'}</div>
                  <div><strong>Συνολικές Απαντήσεις:</strong> {Object.keys(testResult.answers || {}).length}</div>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      })}
    </div>
  );
}